import React, { useState } from "react";
import { MapPin, Lock } from "react-feather";
import { Spinner } from "reactstrap";
import toast from "react-hot-toast";
import { useUpdateMyLocationMutation } from "../../service";
import { captureLocation } from "../../utils/captureLocation";
import "./locationGate.scss";

// Blocking prompt: employees must share their work location while on duty.
// Stays up until location is captured; denial shows how to re-enable.
function LocationGate() {
  const [shared, setShared] = useState(() => {
    try {
      return sessionStorage.getItem("xv:locSent") === "1";
    } catch (e) {
      return true; // storage unavailable — never hard-lock
    }
  });
  const [busy, setBusy] = useState(false);
  const [denied, setDenied] = useState(false);
  const [updateMyLocation] = useUpdateMyLocationMutation();

  if (shared) return null;

  const share = async () => {
    setBusy(true);
    setDenied(false);
    try {
      const loc = await captureLocation();
      let saved = true;
      try {
        await updateMyLocation(loc).unwrap();
      } catch (e) {
        // don't trap the user, but don't pretend it worked either —
        // leave the session flag unset so we retry on the next login
        saved = false;
        console.error("Location save failed:", e);
      }
      if (saved) {
        try {
          sessionStorage.setItem("xv:locSent", "1");
        } catch (e) {}
        toast.success(
          loc.city ? `Location shared: ${loc.city}` : "Location shared"
        );
      } else {
        toast.error("Couldn't save your location. Please tell HR if this keeps happening");
      }
      setShared(true);
    } catch (err) {
      setDenied(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="xv-locgate" role="dialog" aria-modal="true" aria-label="Share your location">
      <div className="xv-locgate-card">
        <span className="xv-locgate-icon">
          {denied ? <Lock size={22} /> : <MapPin size={22} />}
        </span>
        <h2>{denied ? "Location is blocked" : "Share your location"}</h2>
        {denied ? (
          <p>
            Your browser is blocking location access. Click the{" "}
            <strong>lock / location icon in the address bar</strong>, set
            Location to <strong>Allow</strong>, then press Retry.
          </p>
        ) : (
          <p>
            Location sharing is <strong>required while on duty</strong>. Your
            work location is shared only with management and updates once per
            login.
          </p>
        )}
        <button type="button" className="xv-locgate-btn" onClick={share} disabled={busy}>
          {busy ? <Spinner size="sm" /> : denied ? "Retry" : "Share my location"}
        </button>
      </div>
    </div>
  );
}

export default LocationGate;
